// Ficha de un producto en Comisiones pagadas: lo pagado por sus piezas, su costo promedio, su
// costo por semana y quién lo colocó esta semana.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-com-pagadas.html
import { CopyableId } from '@/kit';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { useLivePayout } from '../../api/commissionQueries';
import type { CommissionDirectory } from '../../api/useCommissionDirectory';
import {
    contributorsOf,
    sellersAtLeastTitle,
    sellersChip,
    type ProductPaidRow,
} from '../../lib/paidRows';
import { sharePct } from '../../lib/payoutRows';
import { CATEGORY_LABEL, TIER_LABEL } from '../../lib/ruleText';
import { SECTION_TITLE } from '../fieldStyles';
import { ContributorsList } from './ContributorsList';
import { SeriesSection } from './SeriesSection';

const CHIP = 'rounded-full border border-border px-2 py-0.5 text-[10.5px]';

interface ProductPaidDetailProps {
    row: ProductPaidRow;
    totalCents: number;
    when: string;
    directory: CommissionDirectory;
    /** Qué tanto cuadra la lista de quién aportó con la fila. */
    contributorsNote: string | null;
}

export function ProductPaidDetail({
    row,
    totalCents,
    when,
    directory,
    contributorsNote,
}: ProductPaidDetailProps) {
    const live = useLivePayout();
    const liveReady = live.data !== undefined && !live.isError;
    const contributors = contributorsOf(live.data?.sellers ?? [], 'SKU', row.sku);
    const currentCents = liveReady
        ? contributors.reduce((sum, contributor) => sum + contributor.paidCents, 0)
        : null;
    const averageCents = row.unitsCount > 0 ? Math.round(row.paidCents / row.unitsCount) : null;
    const ofPrice =
        averageCents !== null && row.priceCents !== null && row.priceCents > 0
            ? ` · ${((averageCents / row.priceCents) * 100).toFixed(1)}% del precio`
            : '';

    return (
        <div className="flex h-full min-h-0 flex-col overflow-auto">
            <header className="flex flex-col gap-1 border-b border-border px-4 py-3">
                <CopyableId
                    inline
                    label="SKU"
                    value={row.sku}
                    className="text-[11px] text-muted-foreground"
                />
                <h2 className="truncate text-[15px] font-semibold">{row.name}</h2>
                <p className="text-[24px] font-semibold tabular-nums">
                    {formatMoneyCents(row.paidCents)}
                </p>
                <ul className="flex flex-wrap gap-1" aria-label="Resumen del producto">
                    <li className={CHIP} title={sellersAtLeastTitle(row)}>
                        {sellersChip(row)}
                    </li>
                    <li className={CHIP}>
                        {sharePct(row.paidCents, totalCents).toFixed(1)}% del total · {when}
                    </li>
                </ul>
                <p className="text-[11px] text-muted-foreground">
                    {row.tier ? `Gama ${TIER_LABEL[row.tier]}` : 'Sin gama'} ·{' '}
                    {row.category ? CATEGORY_LABEL[row.category] : 'Sin tipo'} · Precio{' '}
                    {row.priceCents === null ? '—' : formatMoneyCents(row.priceCents)}
                </p>
                <p className="text-[11px]">
                    {averageCents === null
                        ? 'Sin piezas vendidas en el periodo.'
                        : `Costo comisión promedio: ${formatMoneyCents(averageCents)} por pieza${ofPrice}`}
                </p>
            </header>
            <SeriesSection
                by="SKU"
                id={row.sku}
                currentCents={currentCents}
                currentPeriodId={live.data?.periodId}
                currentLoading={live.isLoading}
                title="Comisiones semanales del SKU"
            />
            <section className="px-4 py-3">
                <h3 className={SECTION_TITLE}>Vendedores que lo colocaron esta semana</h3>
                <ContributorsList
                    contributors={contributors}
                    directory={directory}
                    isLoading={live.isLoading}
                    isError={!live.isLoading && !liveReady}
                    note={contributorsNote}
                    limit={10}
                    label="Vendedores que lo colocaron esta semana"
                    empty="Nadie colocó este producto esta semana."
                />
            </section>
        </div>
    );
}
